import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  InputRef,
  Grid,
} from "antd";
import Title from "antd/es/typography/Title";
import { getApi, postApi, putApi } from "redux/apis";
import styles from "./index.module.scss";
import { FORMDATA } from "constants/Data";
import dayjs from "dayjs";
import MissingPartsModal from "../MissingSparePartModel";
import { toastText } from "utils/utils";
import { PlusOutlined } from "@ant-design/icons";

interface AddShipmentManuallyModalProps {
  isVisible: boolean;
  handleCancel: () => void;
  selectedShipment?: any;
  setSelectedShipment: React.Dispatch<React.SetStateAction<any>>;
  fetchAllShipmentDetails: any;
  fetchSalesOrderNumber: any;
  clearFilter: any;
  selectedSalesOrderNumbers: any;
  selectedStatus: any;
}

interface ServiceCenterOption {
  value: string | number;
  label: string;
}

const AddShipmentManuallyModal: React.FC<AddShipmentManuallyModalProps> = ({
  isVisible,
  handleCancel,
  selectedShipment,
  setSelectedShipment,
  fetchAllShipmentDetails,
  fetchSalesOrderNumber,
  clearFilter,
  selectedSalesOrderNumbers,
  selectedStatus,
}) => {
  console.log("SAdkhasd", selectedSalesOrderNumbers);
  console.log("selectedStatus", selectedStatus);
  const { shipmentFields } = FORMDATA;
  const [form] = Form.useForm();
  const [showMissingParts, setShowMissingParts] = useState(false);
  const [missingParts, setMissingParts] = useState([]);
  const [serviceCenters, setServiceCenters] = useState<ServiceCenterOption[]>(
    []
  );
  const [items, setItems] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState([
    { value: "READY_TO_SHIP", label: "Ready to Ship", disabled: false },
    { value: "SHIPPED", label: "Shipped", disabled: true },
  ]);

  const [itemsWithDetails, setItemsWithDetails] = useState<any[]>([]);

  const [itemsArray, setItemsArray] = useState<
    { itemName: string; quantity: string; itemDescription: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);
  const salesOrderInputRef = useRef<InputRef>(null);

  const [serviceCenterLoading, setServiceCentersLoading] = useState(false);
  const [selectedServiceCenter, setSelectedServiceCenter] = useState<
    string | null
  >(null);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  // Determine bodyHeight based on AntD breakpoints
  let bodyHeight: string;

  if (screens.xxl) {
    // anything wider than the typical 15.6" laptop (≥1600px)
    bodyHeight = "60vh";
  } else if (screens.xl) {
    // 15.6" laptop range (>=1200px && <1600px)
    bodyHeight = "75vh";
  } else {
    // smaller screens—choose whatever you need here
    bodyHeight = "75vh";
  }

  useEffect(() => {
    if (isVisible && form.getFieldValue("serviceCenterName")) {
      setTimeout(() => {
        salesOrderInputRef.current?.focus();
      }, 100); // Adding a slight delay ensures focus is set correctly
    }
  }, [isVisible, serviceCenters]);

  // Effect to set form values when selectedShipment changes
  useEffect(() => {
    if (selectedShipment && serviceCenters) {
      console.log("Selected Shipment: ", selectedShipment);
      // const serviceCenterId = serviceCenters?.find(
      //   (center) => center.label === selectedShipment.serviceCenter
      // )?.value;
      const formValues = {
        serviceCenterName: selectedShipment.serviceCenterId,
        salesOrder: selectedShipment.salesOrderNumber,
        salesOrderDate: selectedShipment.salesOrderDate
          ? dayjs(selectedShipment.salesOrderDate)
          : null,
        deliveryDate: selectedShipment.deliveryDate
          ? dayjs(selectedShipment.deliveryDate)
          : null,
        itemNumber: selectedShipment.itemNumber,
        itemDescription: selectedShipment.itemDescription || null,
        status: selectedShipment.status,
        quantity: selectedShipment.quantity.toString(),
      };
      form.setFieldsValue(formValues);
      console.log("formValues", selectedShipment);
      // Set the selected service center state for editing
      setSelectedServiceCenter(selectedShipment.serviceCenterId);

      setStatusOptions(
        selectedShipment.deliveryDate
          ? [
              {
                value: "READY_TO_SHIP",
                label: "Ready to Ship",
                disabled: true,
              },
              { value: "SHIPPED", label: "Shipped", disabled: false },
            ]
          : [
              {
                value: "READY_TO_SHIP",
                label: "Ready to Ship",
                disabled: false,
              },
              { value: "SHIPPED", label: "Shipped", disabled: true },
            ]
      );
      form.setFieldsValue({ status: "READY_TO_SHIP" });
    } else {
      form.resetFields();
      setStatusOptions([
        { value: "READY_TO_SHIP", label: "Ready to Ship", disabled: false },
        { value: "SHIPPED", label: "Shipped", disabled: true },
      ]);
    }

    // const storedServiceCenter = localStorage.getItem("selectedServiceCenter");
    // if (storedServiceCenter) {
    //   const parsedServiceCenter = JSON.parse(storedServiceCenter);
    //   form.setFieldsValue({
    //     serviceCenterName: parsedServiceCenter.value,
    //   });
    //   handleServiceCenterChange(parsedServiceCenter.value, parsedServiceCenter);
    // }
  }, [selectedShipment, form, serviceCenters]);

  // Auto-select service center if only one is available (for new shipments only)
  useEffect(() => {
    if (serviceCenters.length === 1 && !selectedShipment && isVisible) {
      const singleServiceCenter = serviceCenters[0];
      setSelectedServiceCenter(String(singleServiceCenter.value));
      form.setFieldsValue({
        serviceCenterName: singleServiceCenter.value,
      });
      // Trigger the service center change handler
      handleServiceCenterChange(
        String(singleServiceCenter.value),
        singleServiceCenter
      );
    }
  }, [serviceCenters, selectedShipment, isVisible, form]);

  useEffect(() => {
    if (isVisible) {
      const fetchServiceCenters = async () => {
        try {
          setServiceCentersLoading(true);
          const response = await getApi("/serviceCenter/shipment-dropdown");
          if (response.data.success) {
            const transformedCenters = response.data.data.map(
              (center: any) => ({
                value: center.id,
                label: center.name,
              })
            );
            setServiceCenters(transformedCenters);
          }
        } catch (error) {
          console.error("Failed to fetch service centers:", error);
        } finally {
          setServiceCentersLoading(false);
        }
      };

      const fetchItems = async () => {
        try {
          const response = await getApi("/shipment/getProducts?type=true");
          if (response.data.data.length > 0) {
            setItemsWithDetails(response.data.data);
            setItems(
              response.data.data.map((center: any) => ({
                value: center.name,
                label: center.name,
              }))
            );
          }
        } catch (error) {
          console.error("Failed to fetch items:", error);
        }
      };

      fetchServiceCenters();
      fetchItems();

      // Clear localStorage
      localStorage.removeItem("selectedServiceCenter");
    }
  }, [isVisible]);

  const onFieldsChange = () => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    if (deliveryDate) {
      setStatusOptions([
        { value: "READY_TO_SHIP", label: "Ready to Ship", disabled: true },
        { value: "SHIPPED", label: "Shipped", disabled: false },
      ]);
      form.setFieldsValue({ status: "SHIPPED" });
    } else {
      setStatusOptions([
        { value: "READY_TO_SHIP", label: "Ready to Ship", disabled: false },
        { value: "SHIPPED", label: "Shipped", disabled: true },
      ]);
      form.setFieldsValue({ status: "READY_TO_SHIP" });
    }
  };
  const handleAddItem = () => {
    setItemsArray([
      ...itemsArray,
      { itemName: "", quantity: "", itemDescription: "" },
    ]);
  };

  const onFinish = async (values: any) => {
    // Validate itemsArray if it has any items
    if (itemsArray.length > 0) {
      const hasInvalidItems = itemsArray.some(
        (item) => !item.itemName || !item.quantity || Number(item.quantity) <= 0
      );

      if (hasInvalidItems) {
        toastText("Each item must have a quantity greater than 0.", "error");
        return;
      }
    }

    if (values?.quantity && Number(values?.quantity) <= 0) {
      toastText("Each item must have a quantity greater than 0.", "error");
      return;
    }
    try {
      const validItemsArray = itemsArray.filter(
        (item) => item.itemName && item.quantity && Number(item.quantity) > 0
      );
      const payload = {
        serviceCenterId: values.serviceCenterName,
        salesOrderNumber: values.salesOrder.trim(),
        deliveryDate: values.deliveryDate
          ? dayjs(values.deliveryDate)
              .set("hour", dayjs().hour())
              .set("minute", dayjs().minute())
              .set("second", dayjs().second())
              .format("YYYY-MM-DDTHH:mm:ss")
          : null,

        salesOrderDate: values.salesOrderDate
          ? dayjs(values.salesOrderDate)
              .set("hour", dayjs().hour())
              .set("minute", dayjs().minute())
              .set("second", dayjs().second())
              .format("YYYY-MM-DDTHH:mm:ss")
          : null,

        status: values.status,
        items: [
          {
            itemNumber: values.itemNumber,
            quantity: parseInt(values.quantity, 10),
            description: values.itemDescription.trim(),
          },
          ...validItemsArray.map((item) => ({
            itemNumber: item.itemName,
            quantity: parseInt(item.quantity, 10),
            description: item.itemDescription.trim(),
          })),
        ].filter((item) => item.itemNumber && item.quantity > 0),
      };

      let response;
      setIsLoading(true);
      if (selectedShipment) {
        response = await putApi(
          `/shipment/update-shipment/${selectedShipment.id}`,
          payload
        );
      } else {
        response = await postApi("/shipment/create-shipment", payload);
        console.log("payload", payload);
      }
      const message =
        response?.data?.message || "Shipment created successfully.";
      toastText(message, "success");
      form.resetFields();
      setItemsArray([]);
      handleCancel();
      setSelectedShipment(null);
      fetchSalesOrderNumber();
      fetchAllShipmentDetails({
        salesOrderNumber: selectedSalesOrderNumbers,
        status: selectedStatus,
        forceRefresh: true,
        skipDuplicateCheck: true,
      });
      // clearFilter();
    } catch (error: any) {
      if (error.response?.data?.error?.additionalInfo?.missingParts) {
        setMissingParts(error.response.data.error.additionalInfo.missingParts);
        setShowMissingParts(true);
      } else {
        const message =
          error.response?.data?.error?.message ||
          "Something went wrong while creating shipment";
        toastText(message, "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // FIXED: This function now properly updates both local state and form fields
  const handleItemChangea = (
    index: number,
    field: "itemName" | "quantity" | "itemDescription",
    value: string
  ) => {
    const updatedItems = [...itemsArray];
    updatedItems[index][field] = value;

    if (field === "itemName") {
      const selectedItem = itemsWithDetails.find((item) => item.name === value);
      const description = selectedItem?.description || "";
      updatedItems[index].itemDescription = description;

      // Update the form field for item description
      form.setFieldsValue({
        items: {
          [index]: {
            ...form.getFieldValue(["items", index]),
            itemDescription: description,
          },
        },
      });
    }

    console.log("updatedItems: ", updatedItems);
    setItemsArray(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = itemsArray.filter((_, i) => i !== index);
    setItemsArray(updatedItems);
    form.setFieldsValue({
      items: {
        [index]: {
          itemName: undefined,
          quantity: undefined,
          itemDescription: undefined,
        },
      },
    });
  };

  const handleItemChange = (selectedItemName: string) => {
    const selectedItem = itemsWithDetails.find(
      (item) => item.name === selectedItemName
    );

    form.setFieldValue("itemDescription", selectedItem?.description || "");
  };

  const handleServiceCenterChange = async (value: string, option: any) => {
    const selectedServiceCenter = {
      value: value,
      label: option.label, // ✅ correct assignment
    };

    setSelectedServiceCenter(value);
    localStorage.setItem(
      "selectedServiceCenter",
      JSON.stringify(selectedServiceCenter)
    );
    form.setFieldsValue({ serviceCenterName: value });
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={() => {
          handleCancel();
          setSelectedShipment(null);
          form.resetFields();
          setItemsArray([]);
          setSelectedServiceCenter(null);
        }}
        footer={null}
        maskClosable={false}
        closable={true}
        centered={true}
        width="900px"
        bodyStyle={{
          padding: 0,
          height: bodyHeight,
          display: "flex",
          flexDirection: "column",
        }}
        style={{ top: 20 }}
        destroyOnClose={true}
      >
        <div
          className={styles["shipment-modal"]}
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Fixed Header */}
          <div
            className={styles["modal-header"]}
            style={{
              flexShrink: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {selectedShipment ? "Edit Shipment" : "Add Shipment Manually"}
              </Title>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            className={styles["shipment-modal__table"]}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "15px",
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFieldsChange={onFieldsChange}
              initialValues={{
                serviceCenterName: null,
                salesOrder: "",
                itemName: null,
                itemDescription: "",
                quantity: "",
                deliveryDate: null,
                status: "READY_TO_SHIP",
                salesOrderDate: null,
              }}
            >
              {/* Main Information Section */}
              <div className={styles["form-section"]}>
                <Row gutter={[16, 8]}>
                  {/* First Row - Service Center and Sales Order */}
                  <Col span={12} style={{ marginBottom: 10 }}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Service Center
                        <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="serviceCenterName"
                        rules={[
                          {
                            required: true,
                            message: "Please select Service Center",
                          },
                        ]}
                      >
                        <Select
                          size="large"
                          placeholder="Select Service Center"
                          options={serviceCenters}
                          showSearch
                          optionFilterProp="label"
                          value={selectedServiceCenter}
                          onChange={handleServiceCenterChange}
                          loading={serviceCenterLoading}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col span={12} style={{ marginBottom: 10 }}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Sales Order
                        <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="salesOrder"
                        rules={[
                          {
                            required: true,
                            message: "Please enter Sales Order",
                          },
                        ]}
                      >
                        <Input
                          ref={salesOrderInputRef}
                          size="large"
                          placeholder="Enter Sales Order"
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  {/* Second Row - Delivery Date and Status */}
                  <Col span={12} style={{ marginBottom: 10 }}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Delivery Date
                      </label>
                      <Form.Item name="deliveryDate">
                        <DatePicker
                          size="large"
                          style={{ width: "100%" }}
                          format="MM/DD/YYYY"
                          placeholder="Select Delivery Date"
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col span={12} style={{ marginBottom: 10 }}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Select Status
                      </label>
                      <Form.Item name="status">
                        <Select
                          size="large"
                          placeholder="Select Status"
                          options={statusOptions}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col span={24} style={{ marginBottom: 10 }}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Sales Order Date
                        {!selectedShipment && (
                          <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                            *
                          </span>
                        )}
                      </label>
                      <Form.Item
                        name="salesOrderDate"
                        rules={[
                          {
                            required: !selectedShipment,
                            message: "Please select sales order date",
                          },
                        ]}
                      >
                        <DatePicker
                          size="large"
                          style={{ width: "49%" }} // <<< use 50% here
                          format="MM/DD/YYYY"
                          placeholder="Select Sales Order Date"
                          disabled={!!selectedShipment}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  {/* Third Row - Item Details in one line */}
                  <Col span={8}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Item Name
                        <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="itemNumber"
                        rules={[
                          { required: true, message: "Select Item Name" },
                        ]}
                      >
                        <Select
                          size="large"
                          placeholder="Select Item Name"
                          showSearch
                          optionFilterProp="label"
                          options={items}
                          onChange={handleItemChange}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Quantity
                        <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                          *
                        </span>
                      </label>
                      <Form.Item
                        name="quantity"
                        rules={[
                          { required: true, message: "Please enter Quantity" },
                        ]}
                      >
                        <Input
                          size="large"
                          type="number"
                          placeholder="Enter Quantity"
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div className={styles["form-field"]}>
                      <label className={styles["shipment-modal--label"]}>
                        Item Description
                      </label>
                      <Form.Item name="itemDescription">
                        <Input
                          size="large"
                          placeholder="Enter Item Description"
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Additional Items Section */}
              {itemsArray.length > 0 && (
                <div className={styles["form-section"]}>
                  {itemsArray.map((item, index) => (
                    <Row
                      gutter={[16, 8]}
                      key={index}
                      className={styles["additional-item-row"]}
                    >
                      {/* Item Details - Matching main form layout */}
                      <Col span={8}>
                        <label className={styles["shipment-modal--label"]}>
                          Item Name
                          <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                            *
                          </span>
                        </label>
                        <Form.Item
                          name={["items", index, "itemName"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select Item Name",
                            },
                          ]}
                        >
                          <Select
                            size="large"
                            placeholder="Select Item Name"
                            showSearch
                            optionFilterProp="label"
                            options={items}
                            value={item.itemName}
                            onChange={(value) =>
                              handleItemChangea(index, "itemName", value)
                            }
                          />
                        </Form.Item>
                      </Col>

                      <Col span={8}>
                        <label className={styles["shipment-modal--label"]}>
                          Quantity
                          <span style={{ marginLeft: "4px", color: "#ff4d4f" }}>
                            *
                          </span>
                        </label>
                        <Form.Item
                          name={["items", index, "quantity"]}
                          rules={[
                            {
                              required: true,
                              message: "Please enter Quantity",
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            type="number"
                            placeholder="Enter Quantity"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChangea(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        </Form.Item>
                      </Col>

                      <Col span={8}>
                        <label className={styles["shipment-modal--label"]}>
                          Item Description
                        </label>
                        <Form.Item name={["items", index, "itemDescription"]}>
                          <Input
                            size="large"
                            placeholder="Enter Item Description"
                            value={item.itemDescription}
                            onChange={(e) =>
                              handleItemChangea(
                                index,
                                "itemDescription",
                                e.target.value
                              )
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        span={24}
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          alignItems: "flex-end",
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        <Button
                          type="text"
                          danger
                          onClick={() => handleRemoveItem(index)}
                          size="small"
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              )}

              {/* Add Item Button - Updated style */}
              {!selectedShipment && (
                <Button
                  type="dashed"
                  onClick={handleAddItem}
                  style={{
                    width: "20%",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <PlusOutlined /> Add New Item
                </Button>
              )}
            </Form>
          </div>

          {/* Fixed Footer */}
          <div
            className={styles["shipment-modal__footer"]}
            style={{
              flexShrink: 0,
              padding: "15px",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              zIndex: 1,
            }}
          >
            <Button
              className="cancel-button"
              size="large"
              onClick={() => {
                handleCancel();
                setSelectedShipment(null);
                form.resetFields();
                setItemsArray([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              loading={isLoading}
              onClick={() => form.submit()}
              className="save-button"
            >
              {selectedShipment ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </Modal>
      {missingParts?.length > 0 && (
        <MissingPartsModal
          isVisible={showMissingParts}
          handleClose={() => setShowMissingParts(false)}
          missingParts={missingParts}
        />
      )}
    </>
  );
};

export default AddShipmentManuallyModal;
